import React, {useEffect, useRef, useState} from 'react';
import {Layout, Table, Popconfirm} from "antd";
import {useToast} from "@chakra-ui/react";
import {Layout as AntLayout} from "antd";
import Headerbar from "../navBars/Headerbar";
import Sidebar from "../navBars/Sidebar";
import {MDBCol, MDBIcon, MDBRow} from "mdbreact";
import {useAuthState} from "react-firebase-hooks/auth";
import {getAuth} from "firebase/auth";
import Firebase from "../Firebase/Firebase";
import {doc, getDoc, getFirestore, collection, updateDoc} from "firebase/firestore";
import {logAdminAction} from "../utils/auditLog";
import {getDownloadURL, uploadBytes, getStorage, ref} from "firebase/storage";
import {useCollection} from "react-firebase-hooks/firestore";
import {Button} from "@nextui-org/react";
import {Tag} from "primereact/tag";
import {ColumnsType} from "antd/lib/table";
import ChangeEmail from "../Drawers/Profile/ChangeEmail";
import ChangePassword from '../Drawers/Profile/ChangePassword';
import AddAdminUser from '../Drawers/Profile/AddAdminUser';
import placeholder from "../images/holder.jpg";
import moment from "moment";


const {Content} = AntLayout;
const db      = getFirestore(Firebase);
const storage = getStorage(Firebase);
const auth    = getAuth(Firebase);

// ─── Role definitions ─────────────────────────────────────────────────────────

export const ROLES = ['super_admin', 'operations', 'content', 'support', 'finance'] as const;
export type AdminRole = typeof ROLES[number];

export const ROLE_META: Record<AdminRole, {label: string; color: string; description: string}> = {
    super_admin: {label: 'Super Admin',  color: '#29327b', description: 'Full system access'},
    operations:  {label: 'Operations',  color: '#0284c7', description: 'Orders, sellers, payouts'},
    content:     {label: 'Content',     color: '#7c3aed', description: 'Banners, categories, promotions'},
    support:     {label: 'Support',     color: '#0891b2', description: 'Read-only across all features'},
    finance:     {label: 'Finance',     color: '#059669', description: 'Payment config, transaction view'},
};

export const ROLE_PERMISSIONS: Record<AdminRole, Record<string, Record<string, boolean>>> = {
    super_admin: {
        transactions: {view:true,  change_status:true,  payout:true,  refund:true},
        promotions:   {view:true,  quote:true,  approve:true,  reject:true,  stop:true},
        sellers:      {view:true,  edit:true,   verify:true,  suspend:true},
        products:     {view:true,  change_status:true},
        content:      {view:true,  edit:true},
        settings:     {view:true,  edit:true},
        admins:       {view:true,  manage:true},
    },
    operations: {
        transactions: {view:true,  change_status:true,  payout:true,  refund:true},
        promotions:   {view:true,  quote:true,  approve:true,  reject:true,  stop:true},
        sellers:      {view:true,  edit:true,   verify:true,  suspend:true},
        products:     {view:true,  change_status:true},
        content:      {view:true,  edit:false},
        settings:     {view:false, edit:false},
        admins:       {view:false, manage:false},
    },
    content: {
        transactions: {view:false, change_status:false, payout:false, refund:false},
        promotions:   {view:true,  quote:true,  approve:true,  reject:true,  stop:true},
        sellers:      {view:true,  edit:false,  verify:false, suspend:false},
        products:     {view:true,  change_status:true},
        content:      {view:true,  edit:true},
        settings:     {view:false, edit:false},
        admins:       {view:false, manage:false},
    },
    support: {
        transactions: {view:true,  change_status:false, payout:false, refund:false},
        promotions:   {view:true,  quote:false, approve:false, reject:false, stop:false},
        sellers:      {view:true,  edit:false,  verify:false, suspend:false},
        products:     {view:true,  change_status:false},
        content:      {view:true,  edit:false},
        settings:     {view:false, edit:false},
        admins:       {view:false, manage:false},
    },
    finance: {
        transactions: {view:true,  change_status:false, payout:true,  refund:true},
        promotions:   {view:true,  quote:false, approve:false, reject:false, stop:false},
        sellers:      {view:true,  edit:false,  verify:false, suspend:false},
        products:     {view:false, change_status:false},
        content:      {view:false, edit:false},
        settings:     {view:true,  edit:true},
        admins:       {view:false, manage:false},
    },
};

// ─── Role badge ────────────────────────────────────────────────────────────────

const RoleBadge = ({role}: {role: string}) => {
    const meta = ROLE_META[role as AdminRole];
    if (!meta) return <span style={{color:'#aaa', fontSize:12}}>{role ?? '—'}</span>;
    return (
        <span style={{
            display:'inline-block', fontSize:11, fontWeight:600, padding:'2px 10px',
            borderRadius:20, background: meta.color + '18', color: meta.color,
            border: `1px solid ${meta.color}44`,
        }}>
            {meta.label}
        </span>
    );
};

// ─── Permissions grid ──────────────────────────────────────────────────────────

const PermissionsGrid = ({role}: {role: AdminRole}) => {
    const perms = ROLE_PERMISSIONS[role];
    if (!perms) return null;
    const features = [
        {key:'transactions', label:'Transactions',  actions:['view','change_status','payout','refund']},
        {key:'promotions',   label:'Promotions',    actions:['view','quote','approve','reject','stop']},
        {key:'sellers',      label:'Sellers',       actions:['view','edit','verify','suspend']},
        {key:'products',     label:'Products',      actions:['view','change_status']},
        {key:'content',      label:'Content',       actions:['view','edit']},
        {key:'settings',     label:'Settings',      actions:['view','edit']},
        {key:'admins',       label:'Admin Mgmt',    actions:['view','manage']},
    ];
    return (
        <div style={{marginTop:8}}>
            {features.map(f => {
                const fp = perms[f.key] ?? {};
                const enabled = f.actions.filter(a => fp[a]);
                if (enabled.length === 0) return null;
                return (
                    <div key={f.key} style={{display:'flex', alignItems:'center', gap:6, marginBottom:4}}>
                        <span style={{fontSize:11, color:'#888', width:90, flexShrink:0}}>{f.label}</span>
                        {enabled.map(a => (
                            <span key={a} style={{
                                fontSize:10, background:'#f0fdf4', color:'#166534',
                                borderRadius:4, padding:'1px 6px', border:'1px solid #bbf7d0',
                            }}>{a.replace('_',' ')}</span>
                        ))}
                    </div>
                );
            })}
        </div>
    );
};

// ─── Main Profile page ─────────────────────────────────────────────────────────

const Profile = () => {
    const toast = useToast();
    const [collapsed, setCollapsed] = useState(false);
    const [user]     = useAuthState(auth);
    const [profile, setProfile]         = useState<any>(null);
    const [imageUrl, setImageUrl]        = useState<string | null>(null);
    const [uploading, setUploading]      = useState(false);
    const [editRole, setEditRole]        = useState<{uid:string; current:AdminRole} | null>(null);
    const [emailChange, setEmailChange]  = useState(false);
    const [pwChange, setPwChange]        = useState(false);
    const [addAdmin, setAddAdmin]        = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const [userSnapshot, loading] = useCollection(
        collection(db, 'users'),
        {snapshotListenOptions: {includeMetadataChanges: true}}
    );

    // Current logged-in admin's profile
    useEffect(() => {
        if (!user) return;
        getDoc(doc(db, 'users', user.uid)).then(snap => {
            if (snap.exists()) {
                setProfile(snap.data());
                getDownloadURL(ref(storage, `profiles/${user.uid}`))
                    .then(url => setImageUrl(url))
                    .catch(() => {});
            }
        });
    }, [user]);

    // Admin list — anyone with userRole:"Admin" or isAdmin:true
    const admins: any[] = (userSnapshot?.docs ?? [])
        .map(d => ({id: d.id, ...d.data()}))
        .filter((u: any) => u.userRole === 'Admin' || u.isAdmin === true);

    // Super-admin if: explicit role set, OR legacy account with isAdmin:true and no role assigned,
    // OR userRole field has any casing of "Admin"
    const isSuperAdmin =
        profile?.adminRole === 'super_admin' ||
        (profile?.isAdmin === true && !profile?.adminRole) ||
        (profile?.userRole ?? '').toLowerCase() === 'admin';

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !user) return;
        setUploading(true);
        uploadBytes(ref(storage, `profiles/${user.uid}`), file)
            .then(() => {
                setImageUrl(URL.createObjectURL(file));
                toast({position:'top', title:'Profile photo updated', status:'success', duration:2000, isClosable:true});
            })
            .finally(() => setUploading(false));
    };

    const handleDisable = async (uid: string) => {
        const target = admins.find(a => a.id === uid);
        try {
            await updateDoc(doc(db, 'users', uid), {userStatus: 'disabled', isAdmin: false});
            await logAdminAction('admin_disabled', {
                targetId: uid, targetName: target?.fullName ?? target?.emailAuth,
                targetType: 'admin',
            });
            toast({position:'top', title:'Admin disabled', status:'success', duration:2000, isClosable:true});
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        }
    };

    const handleRoleChange = async (uid: string, role: AdminRole) => {
        const target = admins.find(a => a.id === uid);
        const prevRole = target?.adminRole ?? 'super_admin';
        try {
            await updateDoc(doc(db, 'users', uid), {
                adminRole: role,
                permissions: ROLE_PERMISSIONS[role],
            });
            await logAdminAction('admin_role_changed', {
                targetId: uid, targetName: target?.fullName ?? target?.emailAuth,
                targetType: 'admin', from: prevRole, to: role,
            });
            toast({position:'top', title:`Role updated to ${ROLE_META[role].label}`, status:'success', duration:2000, isClosable:true});
            setEditRole(null);
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        }
    };

    const adminColumns: ColumnsType<any> = [
        {
            title: 'Admin',
            key: 'name',
            render: (r: any) => (
                <div>
                    <div style={{fontWeight:600, fontSize:13}}>{r.fullName ?? r.firstname ?? '—'}</div>
                    <div style={{fontSize:12, color:'#888'}}>{r.emailAuth ?? r.email ?? ''}</div>
                </div>
            ),
        },
        {
            title: 'Role',
            key: 'role',
            render: (r: any) => (
                <div>
                    <RoleBadge role={r.adminRole ?? (r.isAdmin ? 'super_admin' : 'support')} />
                    {editRole?.uid === r.id && (
                        <div style={{marginTop:6}}>
                            <select
                                value={editRole!.current}
                                onChange={e => setEditRole({...editRole!, current: e.target.value as AdminRole})}
                                style={{fontSize:12, padding:'2px 6px', borderRadius:6, border:'1px solid #ddd', marginRight:6}}
                            >
                                {ROLES.map(role => (
                                    <option key={role} value={role}>{ROLE_META[role].label}</option>
                                ))}
                            </select>
                            <button style={{fontSize:11, color:'#22c55e', border:'none', background:'none', cursor:'pointer', marginRight:4}}
                                    onClick={() => handleRoleChange(r.id, editRole!.current)}>Save</button>
                            <button style={{fontSize:11, color:'#aaa', border:'none', background:'none', cursor:'pointer'}}
                                    onClick={() => setEditRole(null)}>Cancel</button>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Permissions',
            key: 'perms',
            render: (r: any) => <PermissionsGrid role={(r.adminRole ?? (r.isAdmin ? 'super_admin' : 'support')) as AdminRole} />,
        },
        {
            title: 'Status',
            key: 'status',
            render: (r: any) => {
                const s = (r.userStatus ?? 'active').toLowerCase();
                return <Tag severity={s === 'active' ? 'success' : 'warning'} value={s} />;
            },
        },
        {
            title: 'Joined',
            key: 'joined',
            render: (r: any) => {
                const d = r.dateAdded ?? r.dateJoined;
                if (!d) return '—';
                const dateVal = d?.toDate ? d.toDate() : d;
                return moment(dateVal).format('DD MMM YYYY');
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (r: any) => (
                <div style={{display:'flex', gap:6}}>
                    {isSuperAdmin && r.id !== user?.uid && (
                        <>
                            <button
                                style={{fontSize:11, color:'#29327b', border:'1px solid #c7d2fe',
                                        borderRadius:6, padding:'3px 8px', cursor:'pointer', background:'#eef2ff'}}
                                onClick={() => setEditRole({uid: r.id, current: (r.adminRole ?? 'super_admin') as AdminRole})}>
                                Edit role
                            </button>
                            {(r.userStatus ?? '').toLowerCase() !== 'disabled' && (
                                <Popconfirm
                                    title="Disable this admin? They will lose access but created data is preserved."
                                    onConfirm={() => handleDisable(r.id)}
                                    okText="Disable" cancelText="Cancel"
                                    okButtonProps={{danger: true}}
                                >
                                    <button style={{fontSize:11, color:'#ef4444', border:'1px solid #fecaca',
                                                    borderRadius:6, padding:'3px 8px', cursor:'pointer', background:'#fef2f2'}}>
                                        Disable
                                    </button>
                                </Popconfirm>
                            )}
                        </>
                    )}
                    {r.id === user?.uid && (
                        <span style={{fontSize:11, color:'#aaa'}}>you</span>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <input type="file" ref={fileRef} accept="image/*" style={{display:'none'}} onChange={handleUpload} />
            <Layout style={{minHeight:'100vh'}}>
                <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
                <Layout className="site-layout">
                    <Sidebar collapsed={collapsed}/>
                    <Content>
                        <ChangeEmail changeEmail={emailChange} closeEmail={setEmailChange}/>
                        <ChangePassword changePassword={pwChange} closePassword={setPwChange}/>
                        <AddAdminUser openDrawer={addAdmin} closeDrawer={setAddAdmin}/>

                        <div style={{padding:'20px 24px'}}>
                            <MDBRow>

                                {/* ── My profile ── */}
                                <MDBCol md="4">
                                    <div style={{background:'#fff', borderRadius:12, border:'1px solid #eee',
                                                 boxShadow:'0 1px 4px rgba(0,0,0,.06)', padding:24, marginBottom:16}}>
                                        <div style={{display:'flex', flexDirection:'column', alignItems:'center', marginBottom:20}}>
                                            <div style={{position:'relative', cursor:'pointer', marginBottom:12}}
                                                 onClick={() => fileRef.current?.click()}>
                                                <img
                                                    src={imageUrl ?? placeholder}
                                                    alt="profile"
                                                    style={{width:100, height:100, borderRadius:'50%',
                                                            objectFit:'cover', border:'3px solid #eee'}}
                                                    onError={e => { (e.target as HTMLImageElement).src = placeholder; }}
                                                />
                                                <div style={{
                                                    position:'absolute', bottom:0, right:0,
                                                    background:'#29327b', borderRadius:'50%',
                                                    width:28, height:28, display:'flex', alignItems:'center',
                                                    justifyContent:'center', color:'#fff', fontSize:13,
                                                }}>✎</div>
                                            </div>
                                            {uploading && <div style={{fontSize:12, color:'#888'}}>Uploading…</div>}
                                            <div style={{fontWeight:700, fontSize:17}}>{profile?.fullName ?? '—'}</div>
                                            <div style={{fontSize:13, color:'#888', marginTop:2}}>{profile?.emailAuth ?? user?.email ?? '—'}</div>
                                            <div style={{marginTop:8}}>
                                                <RoleBadge role={profile?.adminRole ?? 'super_admin'} />
                                            </div>
                                        </div>

                                        <div style={{borderTop:'1px solid #f0f0f0', paddingTop:16}}>
                                            <div style={{fontWeight:600, fontSize:12, color:'#888',
                                                         letterSpacing:.4, marginBottom:10}}>YOUR PERMISSIONS</div>
                                            <PermissionsGrid role={(profile?.adminRole ?? 'super_admin') as AdminRole} />
                                        </div>
                                    </div>

                                    {/* Account actions */}
                                    <div style={{background:'#fff', borderRadius:12, border:'1px solid #eee',
                                                 boxShadow:'0 1px 4px rgba(0,0,0,.06)', padding:20}}>
                                        <div style={{fontWeight:600, fontSize:13, color:'#555',
                                                     letterSpacing:.4, marginBottom:12}}>ACCOUNT SETTINGS</div>
                                        <div style={{display:'flex', flexDirection:'column', gap:8}}>
                                            <Button size="sm" flat color="primary"
                                                    onPress={() => setEmailChange(true)}>
                                                Change Email
                                            </Button>
                                            <Button size="sm" flat color="default"
                                                    onPress={() => setPwChange(true)}>
                                                Change Password
                                            </Button>
                                        </div>
                                    </div>
                                </MDBCol>

                                {/* ── Admin management ── */}
                                <MDBCol md="8">
                                    <div style={{background:'#fff', borderRadius:12, border:'1px solid #eee',
                                                 boxShadow:'0 1px 4px rgba(0,0,0,.06)', overflow:'hidden'}}>
                                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                                                     padding:'16px 20px', borderBottom:'1px solid #f0f0f0'}}>
                                            <div>
                                                <h4 style={{margin:0, color:'#29327b', fontWeight:700}}>
                                                    Admins
                                                    <span style={{fontSize:13, color:'#888', fontWeight:400, marginLeft:8}}>
                                                        ({admins.length})
                                                    </span>
                                                </h4>
                                                <p style={{margin:'4px 0 0', fontSize:12, color:'#888'}}>
                                                    Manage who has access and what they can do
                                                </p>
                                            </div>
                                            {isSuperAdmin && (
                                                <Button size="sm" color="primary" shadow onPress={() => setAddAdmin(true)}>
                                                    <MDBIcon icon="plus" className="mr-1" /> Add Admin
                                                </Button>
                                            )}
                                        </div>

                                        {/* Role legend */}
                                        <div style={{display:'flex', gap:8, padding:'10px 20px',
                                                     background:'#fafafa', borderBottom:'1px solid #f0f0f0',
                                                     flexWrap:'wrap'}}>
                                            {ROLES.map(r => (
                                                <div key={r} style={{display:'flex', alignItems:'center', gap:4}}>
                                                    <RoleBadge role={r} />
                                                    <span style={{fontSize:11, color:'#888'}}>— {ROLE_META[r].description}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <Table
                                            columns={adminColumns}
                                            dataSource={admins}
                                            loading={loading}
                                            rowKey="id"
                                            size="middle"
                                            pagination={false}
                                        />
                                    </div>
                                </MDBCol>
                            </MDBRow>
                        </div>
                    </Content>
                </Layout>
            </Layout>
        </>
    );
};

export default Profile;
